import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { BlackGhostButton, DangerUniversalButton, DangerButton, GreyUniversalButton, PrimaryButton, SecondaryButton, TealUniversalButton, WhiteGhostButton, WhiteUniversalButton, contentStyles, } from './Button.components';
import { HelixPalette, HelixStyles } from '../../helix';
/**
 * A simple button.
 */
export const Button = forwardRef((props, ref) => {
    const { dataId, disabled = false, label, leftIcon, loading = false, onBlur, onClick, onFocus, rightIcon, size = 'medium', style, tabIndex, title, type = 'contained', variant, } = props;
    // Width of the button, used when setting the button loading state.
    const [buttonWidth, setButtonWidth] = useState(0);
    // The button ref, used when setting the button loading state.
    const buttonRef = useRef(null);
    /**
     * Builds the content to display inside the button element.
     * @returns The content to display inside the button.
     */
    const getButtonContent = () => {
        // Get styles based on button type and size
        const { iconSize, margin, fontStyle, loaderSize } = type === 'ghost'
            ? contentStyles[type]
            : contentStyles[type][size];
        // Set icon properties
        const leftIconProps = Object.assign(Object.assign({}, iconSize), { margin: label ? `0 ${margin} 0 0` : '0' });
        const rightIconProps = Object.assign(Object.assign({}, iconSize), { margin: label ? `0 0 0 ${margin}` : '0' });
        return !loading ? (_jsxs(_Fragment, { children: [leftIcon && React.cloneElement(leftIcon, { style: leftIconProps }), label && (_jsx(Typography, Object.assign({}, fontStyle, HelixStyles.truncate, { "data-id": `${dataId}-label`, component: "span", whiteSpace: "nowrap" }, { children: label }))), rightIcon && React.cloneElement(rightIcon, { style: rightIconProps })] })) : (_jsx(CircularProgress, { "data-id": `${dataId}-button-loading`, size: loaderSize, sx: { color: HelixPalette.neutral60 } }));
    };
    /**
     * Returns the styles for the button based on the size.
     * @returns The style values for the button.
     */
    const getButtonSize = () => {
        switch (size) {
            case 'large':
                return {
                    borderRadius: '0.5rem',
                    px: type === 'universal' ? '0' : '0.75rem',
                    py: type === 'universal' ? '0' : '0.5rem',
                };
            case 'small':
                return {
                    borderRadius: '0.25rem',
                    px: type === 'universal' ? '0' : '0.5rem',
                    py: type === 'universal' ? '0' : '0.25rem',
                };
            default:
                return {
                    borderRadius: '0.375rem',
                    px: type === 'universal' ? '0' : '0.5rem',
                    py: type === 'universal' ? '0' : '0.375rem',
                };
        }
    };
    /**
     * Returns a contained button (destructive, primary or secondary).
     * @returns The contained button element.
     */
    const renderContainedButton = () => {
        const buttonSize = getButtonSize();
        const buttonSx = Object.assign(Object.assign(Object.assign({}, buttonSize), { width: loading ? buttonWidth : 'fit-content', height: 'fit-content' }), style);
        const buttonContent = getButtonContent();
        if (variant === 'danger') {
            return (_jsx(DangerButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-danger-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        if (variant === 'secondary') {
            return (_jsx(SecondaryButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-secondary-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        return (_jsx(PrimaryButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-primary-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
    };
    /**
     * Returns a universal button (teal, grey, white or danger).
     * @returns The universal button element.
     */
    const renderUniversalButton = () => {
        const buttonSize = getButtonSize();
        const buttonContent = getButtonContent();
        const buttonSx = Object.assign(Object.assign(Object.assign({}, buttonSize), { width: loading ? buttonWidth : 'fit-content', height: 'fit-content' }), style);
        if (variant === 'teal') {
            return (_jsx(TealUniversalButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-universal-teal-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        if (variant === 'white') {
            return (_jsx(WhiteUniversalButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-universal-white-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        if (variant === 'danger') {
            return (_jsx(DangerUniversalButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-universal-danger-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        return (_jsx(GreyUniversalButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-universal-grey-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
    };
    /**
     * Returns a ghost button (white or black).
     * @returns The ghost button element.
     */
    const renderGhostButton = () => {
        const buttonSize = {
            borderRadius: '0.25rem',
            px: '0.375rem',
            py: '0.125rem',
        };
        const buttonContent = getButtonContent();
        const buttonSx = Object.assign(Object.assign(Object.assign({}, buttonSize), { width: loading ? buttonWidth : 'fit-content', height: 'fit-content' }), style);
        if (variant === 'white') {
            return (_jsx(WhiteGhostButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-ghost-white-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
        }
        return (_jsx(BlackGhostButton, Object.assign({ ref: ref !== null && ref !== void 0 ? ref : buttonRef, "aria-label": label !== null && label !== void 0 ? label : title, "data-id": `${dataId}-ghost-black-button`, disabled: disabled || loading, onBlur: onBlur, onClick: onClick, onFocus: onFocus, tabIndex: tabIndex, title: title, type: "button", sx: buttonSx }, { children: buttonContent })));
    };
    /**
     * Base render function to render the correct button element based on the button type.
     * @returns The button element.
     */
    const renderButton = () => {
        switch (type) {
            case 'ghost':
                return renderGhostButton();
            case 'universal':
                return renderUniversalButton();
            default:
                return renderContainedButton();
        }
    };
    // Sets the button width on first render. Required for the button loading state.
    useEffect(() => {
        if (buttonRef.current) {
            setButtonWidth(buttonRef.current.offsetWidth);
        }
    }, []);
    return _jsx(_Fragment, { children: renderButton() });
});
export default Button;
